import { Textarea } from '@/components/ui/FormControls';
import { useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Purchase } from '@/hooks/usePurchases';
import { useOperationIntent } from '@/hooks/useOperationIntent';
import { operationsRpc } from '@/lib/operations';
import { refreshSaleConsumers } from '@/lib/refreshSaleConsumers';
import WorkModalShell from '@/components/common/WorkModalShell';
import { purchaseInputClass as input } from './PurchasePaymentFields';
type Preview = {
  purchase_id: string;
  version: string;
  can_delete: boolean;
  active_payment: boolean;
  stock_history: boolean;
};
export default function PurchaseBulkDelete({
  rows,
  label,
  disabled = false,
}: {
  rows: Purchase[];
  label: string;
  disabled?: boolean;
}) {
  const client = useQueryClient();
  const intent = useOperationIntent('purchase-bulk-delete');
  const [open, setOpen] = useState(false),
    [busy, setBusy] = useState(false),
    [previews, setPreviews] = useState<Preview[]>([]),
    [reason, setReason] = useState(''),
    [error, setError] = useState(''),
    [progress, setProgress] = useState('');
  const lock = useRef(false),
    cancel = useRef(false);
  async function prepare() {
    if (lock.current) return;
    lock.current = true;
    cancel.current = false;
    setOpen(true);
    setBusy(true);
    setError('');
    setReason('');
    setPreviews([]);
    const all: Preview[] = [];
    try {
      for (let offset = 0; offset < rows.length; offset += 10) {
        if (cancel.current) break;
        const group = await Promise.all(
          rows.slice(offset, offset + 10).map((p) =>
            operationsRpc<Preview>('preview_purchase_lifecycle_v1', {
              p_data: { purchase_id: p.id, action: 'delete' },
            }),
          ),
        );
        all.push(
          ...group.map((preview, index) =>
            preview.version !== rows[offset + index]?.updated_at
              ? { ...preview, can_delete: false }
              : preview,
          ),
        );
        setProgress(`Reviewed ${all.length} of ${rows.length}`);
      }
      setPreviews(all);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function run() {
    if (lock.current) return;
    lock.current = true;
    cancel.current = false;
    setBusy(true);
    setError('');
    let done = 0;
    const failed: string[] = [];
    const eligible = previews.filter((x) => x.can_delete);
    try {
      for (const p of eligible) {
        if (cancel.current) break;
        try {
          const result = await operationsRpc<{ success: boolean }>('change_purchase_lifecycle_v1', {
            p_data: {
              contract_version: 1,
              purchase_id: p.purchase_id,
              expected_updated_at: p.version,
              action: 'delete',
              reason,
              request_key: intent.key(`delete:${p.purchase_id}:${p.version}`),
            },
          });
          if (!result.success)
            throw new Error('Deletion could not be confirmed. Recover before retrying.');
          intent.complete(`delete:${p.purchase_id}:${p.version}`);
          done++;
        } catch (e) {
          failed.push(
            `${rows.find((x) => x.id === p.purchase_id)?.purchase_number || p.purchase_id}: ${(e as Error).message}`,
          );
        }
        setProgress(
          `${done} moved to bin · ${failed.length} failed · ${previews.length - eligible.length} protected`,
        );
      }
      if (failed.length) setError(failed.join('\n'));
      setPreviews([]);
    } finally {
      await refreshSaleConsumers(client);
      setBusy(false);
      lock.current = false;
    }
  }
  return (
    <>
      <button
        disabled={disabled || !rows.length || busy}
        onClick={() => void prepare()}
        className="min-h-11 rounded-xl border border-destructive/30 px-3 text-sm text-destructive disabled:opacity-40"
      >
        {label}
      </button>
      {open && (
        <WorkModalShell
          open
          onClose={() => setOpen(false)}
          busy={busy}
          title="Review purchase deletion"
        >
          <h2 className="text-lg font-semibold">Review deletion</h2>
          <div className="min-h-0 space-y-3 overflow-y-auto py-4">
            <p className="text-sm text-muted-foreground">
              Scope: {rows.length}{' '}
              {label.startsWith('Delete selected')
                ? 'selected purchases'
                : 'filtered purchases across all pages'}
              . Changed records are skipped and must be reloaded. Only entries without active
              payments or stock history can move to the bin. No permanent deletion.
            </p>
            {progress && (
              <p role="status" className="text-sm">
                {progress}
              </p>
            )}
            {!busy && previews.length > 0 && (
              <>
                <p className="text-sm">
                  Eligible: {previews.filter((p) => p.can_delete).length} · Protected/skipped:{' '}
                  {previews.filter((p) => !p.can_delete).length}
                </p>
                <label className="block space-y-1 text-sm">
                  Reason (3–500 characters)
                  <Textarea
                    value={reason}
                    maxLength={500}
                    onChange={(e) => setReason(e.target.value)}
                    className={input}
                    helpLabel="Reason (3–500 characters)"
                  />
                </label>
              </>
            )}
            {error && (
              <p role="alert" className="whitespace-pre-wrap text-sm text-destructive">
                {error}
              </p>
            )}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            {busy ? (
              <button
                className="min-h-11 rounded-xl border border-border px-4"
                onClick={() => {
                  cancel.current = true;
                }}
              >
                Stop after current entries
              </button>
            ) : (
              <button
                className="min-h-11 rounded-xl border border-border px-4"
                onClick={() => setOpen(false)}
              >
                Close
              </button>
            )}
            <button
              disabled={
                busy || !!error || reason.trim().length < 3 || !previews.some((p) => p.can_delete)
              }
              onClick={() => void run()}
              className="min-h-11 rounded-xl bg-destructive px-4 text-sm text-destructive-foreground disabled:opacity-40"
            >
              Confirm eligible deletion
            </button>
          </div>
        </WorkModalShell>
      )}
    </>
  );
}
